/**
 * La pantalla «Disparadores» del oficial (29/10/2026), del lado puro: el formulario (qué falta para guardar y qué se
 * manda), la regla en palabras mientras se elige y cómo se lee la última corrida. Las reglas de verdad las tiene el
 * backend (`util/disparador.util`, `services/disparadores.service`): esto sólo evita mandar algo que va a rechazar y lo
 * dice en la pantalla.
 *
 * - El mapeo tiene que estar completo y decidido: con un dato del cliente, qué pasa si a alguien le falta. En el
 *   asistente eso se decide cuando el chequeo encuentra a alguien sin el dato; un disparador corre cada vez con otra
 *   gente, así que se decide de antemano (el backend lo exige igual).
 * - La ventana (no le vuelve a escribir a la misma persona por N días) tiene tope según cada cuánto corre: 20 días todos
 *   los días, 140 una vez por semana (son las campañas que se pueden excluir).
 */

import type { MapeoVariables, MotivoExclusion, EstadoCampanaOficial } from "../types";
import { ORDEN_GATES } from "../types";
import type { BaseDeLaCorrida, CadaDisparador, Disparador, DisparadorDeLaCampana, EntradaDisparador, ListaDisparadores, TipoDisparador, UltimaCorridaVista } from "../types.disparadores";
import { MOTIVO_EXCLUSION } from "./textos";
import { validarMapeoLocal } from "./mapeo";
import { num, plural } from "../../campana-nueva/utils/format";

export const RUTA_DISPARADORES = "/activa/oficial/disparadores";
export const HORA_DE_CORRIDA = "09:00";
export const LARGO_NOMBRE_DISPARADOR = 80;
export const DIAS_MAXIMO = 365;
export const VENTANA_MAXIMA: Record<CadaDisparador, number> = { dia: 20, semana: 140 };
/** Cuántos días de atraso acepta la inactividad (`ATRASO_MAXIMO_INACTIVIDAD` del backend): la base tiene que traer las ventas de ayer. */
export const ATRASO_MAXIMO_INACTIVIDAD = 1;
export const DIAS_SEMANA = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"] as const;

/** Lo que tiene el formulario: los números como texto (lo que hay en el campo). */
export interface FormDisparador {
  nombre: string;
  tipo: TipoDisparador;
  dias: string;
  ventanaDias: string;
  cada: CadaDisparador;
  diaSemana: number;
  plantillaId: string;
  mapeo: MapeoVariables;
  audienciaTipo: "publico" | "base";
  publicoId: string;
}

export const FORM_NUEVO: FormDisparador = {
  nombre: "",
  tipo: "inactividad",
  dias: "30",
  ventanaDias: "30",
  cada: "semana",
  diaSemana: 1,
  plantillaId: "",
  mapeo: {},
  audienciaTipo: "publico",
  publicoId: "",
};

/** El formulario de uno que ya está, para editarlo. */
export function formDe(d: Disparador): FormDisparador {
  return {
    nombre: d.nombre,
    tipo: d.tipo,
    dias: String(d.dias),
    ventanaDias: String(d.ventanaDias),
    cada: d.cada,
    diaSemana: d.diaSemana ?? 1,
    plantillaId: d.plantilla?.id || "",
    mapeo: { ...(d.mapeo || {}) },
    audienciaTipo: d.audiencia.tipo,
    publicoId: d.audiencia.publicoId || "",
  };
}

const entero = (s: string): number | null => (/^\d+$/.test(s.trim()) ? Number(s.trim()) : null);

/**
 * Qué falta para guardar, por campo (vacío = se puede). `numeros`: las variables que se mapean (sin las de los botones
 * que cuentan clics); `camposValidos`: los datos de la audiencia elegida.
 */
export function erroresDelForm(f: FormDisparador, numeros: number[], camposValidos?: readonly string[]): Record<string, string> {
  const err: Record<string, string> = {};
  const nombre = f.nombre.trim();
  if (!nombre) err.nombre = "Poné un nombre";
  else if (nombre.length > LARGO_NOMBRE_DISPARADOR) err.nombre = `El nombre va hasta ${LARGO_NOMBRE_DISPARADOR} caracteres`;
  const dias = entero(f.dias);
  if (dias === null || dias < 1 || dias > DIAS_MAXIMO) err.dias = `Poné de 1 a ${DIAS_MAXIMO} días`;
  const ventana = entero(f.ventanaDias);
  const maxima = VENTANA_MAXIMA[f.cada];
  if (ventana === null || ventana < 1 || ventana > maxima) err.ventanaDias = `Poné de 1 a ${maxima} días ${f.cada === "dia" ? "con «todos los días»" : "una vez por semana"}`;
  if (f.audienciaTipo === "base" && !f.publicoId) err.audiencia = "Elegí el público de tu base";
  if (!f.plantillaId) err.plantilla = "Elegí la plantilla";
  else {
    const vars = validarMapeoLocal(numeros, f.mapeo, camposValidos);
    for (const n of numeros) {
      const k = String(n);
      const e = f.mapeo[k];
      if (!vars[k] && e?.fuente === "campo" && !e.siVacio) vars[k] = "Elegí qué pasa si a alguien le falta";
    }
    for (const [k, v] of Object.entries(vars)) err[`var${k}`] = v;
  }
  return err;
}

/** El primer error, para el `title` del botón y el texto de al lado. */
export function motivoParaNoGuardar(err: Record<string, string>): string {
  const [primero] = Object.values(err);
  return primero || "";
}

/** Lo que se manda al backend (crear y editar van enteros). */
export function entradaDe(f: FormDisparador): EntradaDisparador {
  return {
    nombre: f.nombre.trim(),
    tipo: f.tipo,
    dias: Number(f.dias.trim()),
    ventanaDias: Number(f.ventanaDias.trim()),
    cada: f.cada,
    diaSemana: f.cada === "semana" ? f.diaSemana : null,
    plantillaId: f.plantillaId,
    mapeo: f.mapeo,
    audiencia: f.audienciaTipo === "base" ? { tipo: "base", publicoId: f.publicoId } : { tipo: "publico" },
  };
}

/** «A quienes hace más de 30 días que no compran»: la misma frase que el backend, mientras se elige. */
export function quienesDe(tipo: TipoDisparador, dias: number, cada: CadaDisparador): string {
  const d = `${num(dias)} ${plural(dias, "día", "días")}`;
  if (tipo === "inactividad") return `A quienes hace más de ${d} que no compran`;
  return cada === "semana" ? `A quienes compraron por última vez hace entre ${num(dias)} y ${num(dias + 6)} días` : `A quienes compraron por última vez hace ${d}`;
}

/** «Todos los días a las 09:00» o «Los lunes a las 09:00». */
export function cuandoDe(cada: CadaDisparador, diaSemana: number): string {
  if (cada === "dia") return `Todos los días a las ${HORA_DE_CORRIDA}`;
  const dia = (DIAS_SEMANA[diaSemana] ?? "Lunes").toLowerCase();
  return `${dia === "sábado" || dia === "domingo" ? `Los ${dia}s` : `Los ${dia}`} a las ${HORA_DE_CORRIDA}`;
}

/** «No le vuelve a escribir a la misma persona por 30 días». */
export function textoVentana(dias: number): string {
  return `No le vuelve a escribir a la misma persona por ${num(dias)} ${plural(dias, "día", "días")}.`;
}

/** A dónde lleva el link del borrador: el asistente si sigue en borrador, el detalle si ya se confirmó. */
export function rutaDelBorrador(c: { id: string; estado: EstadoCampanaOficial }): string {
  const id = encodeURIComponent(c.id);
  return c.estado === "borrador" ? `/activa/oficial/campanas/${id}/editar` : `/activa/oficial/campanas/${id}`;
}

export type Tono = "ok" | "info" | "alerta" | "error";

/** El título y el tono de la última corrida. */
export function tituloCorrida(u: Pick<UltimaCorridaVista, "resultado" | "entraron">): { titulo: string; tono: Tono } {
  switch (u.resultado) {
    case "armado":
      return { titulo: `Armó un borrador con ${num(u.entraron || 0)} ${plural(u.entraron || 0, "persona", "personas")}`, tono: "ok" };
    case "sin_nadie":
      return { titulo: "Hoy no alcanzaba a nadie", tono: "info" };
    case "esperando":
      return { titulo: "No armó otro: espera al anterior", tono: "info" };
    case "frenado":
      return { titulo: "No armó nada", tono: "alerta" };
    default:
      return { titulo: "Falló la corrida", tono: "error" };
  }
}

/** Quiénes quedaron afuera, en el orden del chequeo, con el título de cada motivo (sólo los que sacaron a alguien). */
export function filasFuera(fuera: UltimaCorridaVista["fuera"]): { motivo: MotivoExclusion; titulo: string; cantidad: number }[] {
  return ORDEN_GATES.filter((m) => Number(fuera?.[m]) > 0).map((m) => ({ motivo: m, titulo: MOTIVO_EXCLUSION[m]?.titulo || m, cantidad: Number(fuera[m]) }));
}

const ddmm = (f: string) => `${f.slice(8, 10)}/${f.slice(5, 7)}`;

/**
 * «Probar ahora» no es un simulacro: arma el borrador del día, y mientras ése no termine el disparador no arma otro. La
 * tarjeta lo dice al lado del botón, antes de tocarlo.
 */
export const TEXTO_PROBAR_AHORA = "«Probar ahora» arma el borrador de hoy: hasta que lo confirmes o lo canceles, el disparador no arma otro.";

/** «la base de ventas hasta el 28/10 (1 día de atraso)»; null si no se sabe. */
function baseEnPalabras(base: BaseDeLaCorrida | null | undefined): string | null {
  if (!base?.hasta) return null;
  const atraso = base.dias === null ? "" : base.dias === 0 ? " (al día)" : ` (${num(base.dias)} ${plural(base.dias, "día", "días")} de atraso)`;
  return `la base de ventas hasta el ${ddmm(base.hasta)}${atraso}`;
}

/** Con qué base corrió, en la tarjeta de la última corrida; null si no la miró. */
export function textoBaseDeLaCorrida(base: BaseDeLaCorrida | null | undefined): string | null {
  const b = baseEnPalabras(base);
  return b ? `Corrió con ${b}.` : null;
}

/** El renglón de un borrador de un disparador en el asistente: de cuál es, qué día lo armó y con qué base (el atraso). */
export function textoDelBorradorDeDisparador(de: DisparadorDeLaCampana): string {
  const b = baseEnPalabras(de.base);
  return `Lo armó el disparador «${de.nombre}» el ${ddmm(de.dia)}${b ? ` con ${b}` : ""}: revisalo antes de confirmarlo.`;
}

/**
 * Lo que se dice arriba de la base de ventas en uso: sin ella, o vieja, los disparadores no arman nada; con más de un
 * día de atraso, los de inactividad tampoco (`conInactividad`: si hay alguno).
 */
export function avisoBase(base: ListaDisparadores["base"], conInactividad = false): { texto: string; alerta: boolean } {
  if (!base) return { texto: "No hay una base de ventas en uso: los disparadores no arman nada hasta que la subas en Público.", alerta: true };
  if (!base.hasta || base.dias === null) return { texto: "La base de ventas en uso no dice hasta qué día trae ventas: volvé a subirla para que los disparadores sepan quién compró.", alerta: true };
  const hace = base.dias === 0 ? "de hoy" : `de hace ${num(base.dias)} ${plural(base.dias, "día", "días")}`;
  if (base.vieja) {
    return { texto: `La base de ventas en uso trae ventas hasta el ${ddmm(base.hasta)} (${hace}): está desactualizada y los disparadores no arman nada con ella. Subí una más nueva en Público.`, alerta: true };
  }
  if (conInactividad && base.dias > ATRASO_MAXIMO_INACTIVIDAD) {
    return {
      texto: `La base de ventas en uso trae ventas hasta el ${ddmm(base.hasta)} (${hace}): los de inactividad no arman nada con ella, porque necesitan las ventas de ayer. Subí una más nueva en Público.`,
      alerta: true,
    };
  }
  return { texto: `La base de ventas en uso trae ventas hasta el ${ddmm(base.hasta)} (${hace}).`, alerta: false };
}
